import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EditorSelect from './EditorSelect';
import DbDatePicker from '../DbDatePicker';
import { byDayCodeForDate } from '../../utils/calendarRecurrence';
import { fromDateKey, toDateKey, addMonths, weekdayLabelForByDay } from '../../utils/calendarDates';

// KEY tables, resolved at RENDER (a module-scope `t()` freezes English at
// import time and never follows a language switch).
const FREQ_OPTION_KEYS = [
  { value: 'none', labelKey: 'calendar.recurrence.none' },
  { value: 'daily', labelKey: 'calendar.recurrence.daily' },
  { value: 'weekly', labelKey: 'calendar.recurrence.weekly' },
  { value: 'monthly', labelKey: 'calendar.recurrence.monthly' },
];
const END_OPTION_KEYS = [
  { value: 'never', labelKey: 'calendar.recurrence.never' },
  { value: 'until', labelKey: 'calendar.recurrence.onDate' },
  { value: 'count', labelKey: 'calendar.recurrence.afterN' },
];
/** RRULE BYDAY codes — protocol tokens, they stay MO/TU/… on the wire. Only
 *  the CHIP LABEL is localised, from Intl's narrow weekday names. */
const BYDAY_CODES = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
const FREQ_UNIT_KEYS = {
  daily: 'calendar.recurrence.dayUnit',
  weekly: 'calendar.recurrence.weekUnit',
  monthly: 'calendar.recurrence.monthUnit',
};

/**
 * Repeat control for the event editor (Phase 4) — the round-30.08 chrome
 * only: EditorSelect for frequency/end mode, modal-input steppers, BYDAY
 * weekday chips for weekly, DbDatePicker for the until date. Google's compact
 * layout instincts, no native controls. Extracted per the modular rule — the
 * editor modal is already the largest calendar file.
 *
 * Controlled: `recurrence` is the §3.2 rule (or null) + `seriesStartDay`
 * anchors the BYDAY default and the until picker's opening month.
 */
export default function RecurrenceEditor({ recurrence, onChange, seriesStartDay }) {
  const { t, i18n } = useTranslation();
  const [untilPicker, setUntilPicker] = useState(null); // anchorRect
  const freqOptions = FREQ_OPTION_KEYS.map((o) => ({ value: o.value, label: t(o.labelKey) }));
  const endOptions = END_OPTION_KEYS.map((o) => ({ value: o.value, label: t(o.labelKey) }));
  // [code, chip letter, full weekday name]. The chip shows the narrow letter,
  // but the ACCESSIBLE NAME uses the full name — two narrow labels collide in
  // both locales (T/T for Tue/Thu, S/S for Sat/Sun) and duplicate accessible
  // names are unusable for a screen reader.
  const weekdayChips = useMemo(
    () =>
      BYDAY_CODES.map((code) => [
        code,
        weekdayLabelForByDay(code, 'narrow'),
        weekdayLabelForByDay(code, 'long'),
      ]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language]
  );
  const freq = recurrence?.freq || 'none';
  const interval = recurrence?.interval ?? 1;
  const endMode = recurrence?.count != null ? 'count' : recurrence?.until != null ? 'until' : 'never';

  const defaultByDay = () => [byDayCodeForDate(seriesStartDay || new Date())];

  const setFreq = (value) => {
    if (value === 'none') return onChange(null);
    const next = { freq: value, interval };
    if (recurrence?.count != null) next.count = recurrence.count;
    if (recurrence?.until != null) next.until = recurrence.until;
    if (value === 'weekly') next.by_day = recurrence?.by_day || defaultByDay();
    return onChange(next);
  };

  const patch = (fields) => onChange({ ...recurrence, ...fields });

  const setEndMode = (mode) => {
    const next = { ...recurrence };
    delete next.count;
    delete next.until;
    if (mode === 'count') next.count = 10;
    if (mode === 'until') next.until = toDateKey(addMonths(seriesStartDay || new Date(), 3));
    onChange(next);
  };

  const toggleDay = (code) => {
    const current = recurrence?.by_day || defaultByDay();
    const next = current.includes(code)
      ? current.filter((c) => c !== code)
      : [...current, code];
    if (next.length === 0) return; // at least one day stays selected
    patch({ by_day: next });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <EditorSelect
          ariaLabel={t('calendar.recurrence.repeats')}
          value={freq}
          options={freqOptions}
          onChange={setFreq}
          width={160}
        />
        {freq !== 'none' && (
          <>
            <span style={{ fontSize: 12, color: '#6b7280' }}>{t('calendar.recurrence.every')}</span>
            <input
              className="modal-input"
              type="text"
              inputMode="numeric"
              style={{ width: 64 }}
              aria-label={t('calendar.recurrence.repeatInterval')}
              value={interval}
              onChange={(e) => {
                // Plain text field (owner: no spinner). Digits only while
                // typing; empty allowed so the value can be retyped.
                const digits = e.target.value.replace(/\D/g, '').slice(0, 2);
                patch({ interval: digits === '' ? '' : Number(digits) });
              }}
              onBlur={() => {
                const v = Math.max(1, Math.min(99, Number(interval) || 1));
                if (v !== interval) patch({ interval: v });
              }}
            />
            <span style={{ fontSize: 12, color: '#6b7280' }}>{t(FREQ_UNIT_KEYS[freq])}</span>
          </>
        )}
      </div>

      {freq === 'weekly' && (
        <div style={{ display: 'flex', gap: 4 }} role="group" aria-label={t('calendar.recurrence.repeatOn')}>
          {weekdayChips.map(([code, letter, fullName]) => {
            const active = (recurrence?.by_day || defaultByDay()).includes(code);
            return (
              <button
                key={code}
                type="button"
                className="cal-btn cal-btn--icon"
                aria-label={t('calendar.recurrence.repeatOnDay', { day: fullName })}
                aria-pressed={active}
                style={
                  active
                    ? { background: 'var(--accent)', borderColor: 'var(--accent)', color: 'var(--accent-fg, #fff)', width: 26, justifyContent: 'center' }
                    : { width: 26, justifyContent: 'center' }
                }
                onClick={() => toggleDay(code)}
              >
                {letter}
              </button>
            );
          })}
        </div>
      )}

      {freq !== 'none' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: '#6b7280' }}>{t('calendar.recurrence.ends')}</span>
          <EditorSelect
            ariaLabel={t('calendar.recurrence.ends')}
            value={endMode}
            options={endOptions}
            onChange={setEndMode}
            width={140}
          />
          {endMode === 'until' && (
            <button
              type="button"
              className="modal-input"
              style={{ width: 130, textAlign: 'left', cursor: 'pointer' }}
              aria-label={t('calendar.recurrence.repeatUntil')}
              onClick={(e) => setUntilPicker(e.currentTarget.getBoundingClientRect())}
            >
              {recurrence.until}
            </button>
          )}
          {endMode === 'count' && (
            <>
              <input
                className="modal-input"
                type="text"
                inputMode="numeric"
                style={{ width: 72 }}
                aria-label={t('calendar.recurrence.repeatCount')}
                value={recurrence.count}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 3);
                  patch({ count: digits === '' ? '' : Number(digits) });
                }}
                onBlur={() => {
                  const v = Math.max(1, Math.min(999, Number(recurrence.count) || 1));
                  if (v !== recurrence.count) patch({ count: v });
                }}
              />
              <span style={{ fontSize: 12, color: '#6b7280' }}>{t('calendar.recurrence.times')}</span>
            </>
          )}
        </div>
      )}

      {untilPicker && (
        <DbDatePicker
          kind="date"
          value={recurrence?.until || toDateKey(seriesStartDay || new Date())}
          anchorRect={untilPicker}
          onPick={(value) => {
            // Never before the series start (a rule that ends before it
            // begins is the malformed-passthrough path, not a UI state).
            const startKey = toDateKey(seriesStartDay || fromDateKey(value) || new Date());
            patch({ until: value < startKey ? startKey : value });
            setUntilPicker(null);
          }}
          onClear={() => setUntilPicker(null)}
          onClose={() => setUntilPicker(null)}
        />
      )}
    </div>
  );
}
